// Custom Shopify Inbox launcher.
// Keeps Shopify's native chat experience while replacing its activator icon.

(function () {
  const CUSTOM_ICON_ID = 'custom-chat-icon';
  const NEW_CHAT_SELECTOR = 'shopify-chat';
  const LEGACY_CHAT_SELECTOR = '#shopify-chat inbox-online-store-chat, inbox-online-store-chat#ShopifyChat, inbox-online-store-chat';
  const RETRY_DELAY = 250;
  const MAX_RETRIES = 80;

  let customChatIcon = null;
  let chatElement = null;
  let chatElementObserver = null;
  let documentObserver = null;
  let retryTimer = null;
  let retryCount = 0;

  function findInOpenShadowRoots(root, selector) {
    if (!root || typeof root.querySelector !== 'function') {
      return null;
    }

    const directMatch = root.querySelector(selector);
    if (directMatch) {
      return directMatch;
    }

    const descendants = root.querySelectorAll('*');

    for (const descendant of descendants) {
      if (!descendant.shadowRoot) {
        continue;
      }

      const shadowMatch = findInOpenShadowRoots(descendant.shadowRoot, selector);
      if (shadowMatch) {
        return shadowMatch;
      }
    }

    return null;
  }

  function getChatElement() {
    return document.querySelector(NEW_CHAT_SELECTOR) || document.querySelector(LEGACY_CHAT_SELECTOR);
  }

  function isNewChatElement(element) {
    return element && element.matches(NEW_CHAT_SELECTOR);
  }

  function isLegacyChatOpen(element) {
    if (!element || !element.shadowRoot) {
      return false;
    }

    const chatApp = findInOpenShadowRoots(element.shadowRoot, '.chat-app');

    return Boolean(
      chatApp &&
        (chatApp.classList.contains('is-open') || chatApp.classList.contains('chat-app--is-open'))
    );
  }

  function isChatOpen() {
    if (!chatElement) {
      return false;
    }

    if (isNewChatElement(chatElement)) {
      return chatElement.hasAttribute('open');
    }

    return isLegacyChatOpen(chatElement);
  }

  function updateChatIcon(open) {
    if (!customChatIcon) {
      return;
    }

    customChatIcon.setAttribute('aria-expanded', String(open));
    customChatIcon.setAttribute('aria-label', open ? 'Close live chat' : 'Open live chat');

    // Preserve the existing mobile design: it always displays the chat artwork.
    if (window.innerWidth < 769) {
      return;
    }

    const image = customChatIcon.querySelector('img');
    if (!image) {
      return;
    }

    const nextSource = open
      ? customChatIcon.dataset.openIconUrl
      : customChatIcon.dataset.closedIconUrl;

    if (nextSource) {
      image.src = nextSource;
    }

    image.alt = open ? 'Close Chat' : 'Live Chat';
  }

  function syncChatState() {
    updateChatIcon(isChatOpen());
  }

  function observeChatElement(element) {
    if (chatElementObserver) {
      chatElementObserver.disconnect();
      chatElementObserver = null;
    }

    if (!element) {
      return;
    }

    chatElementObserver = new MutationObserver(syncChatState);
    chatElementObserver.observe(element, {
      attributes: true,
      attributeFilter: ['open', 'class'],
      childList: true
    });

    if (!isNewChatElement(element) && element.shadowRoot) {
      chatElementObserver.observe(element.shadowRoot, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
      });
    }
  }

  function ensureLegacyStyles(element) {
    if (!element || !element.shadowRoot || isNewChatElement(element)) {
      return;
    }

    if (element.shadowRoot.getElementById('custom-chat-legacy-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'custom-chat-legacy-styles';
    style.textContent = `
      button.chat-toggle-tagline {
        background-color: rgba(255, 255, 255, 0.9) !important;
      }

      button.chat-toggle-text-button {
        font-family: inter !important;
      }

      button.chat-toggle.chat-toggle--text-button,
      button.chat-toggle.chat-toggle--icon-button,
      button[class*="chat-toggle"][class*="text-button"],
      .chat-app .chat-app--close-button-bottom-right {
        opacity: 0 !important;
        pointer-events: none !important;
        display: none !important;
      }

      @media (min-width: 769px) {
        .chat-ui {
          margin-bottom: 45px !important;
        }
      }
    `;

    element.shadowRoot.appendChild(style);
  }

  function refreshChatElement() {
    const nextChatElement = getChatElement();

    if (nextChatElement !== chatElement) {
      chatElement = nextChatElement;
      observeChatElement(chatElement);
    }

    ensureLegacyStyles(chatElement);
    syncChatState();

    if (!chatElement) {
      return false;
    }

    return Boolean(
      isNewChatElement(chatElement)
        ? true
        : getLegacyChatButton(chatElement)
    );
  }

  function stopRetrying() {
    if (retryTimer) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }
  }

  function retryChatDetection() {
    stopRetrying();

    if (refreshChatElement() || retryCount >= MAX_RETRIES) {
      retryCount = 0;
      return;
    }

    retryCount += 1;
    retryTimer = window.setTimeout(retryChatDetection, RETRY_DELAY);
  }

  function scheduleChatDetection() {
    retryCount = 0;
    retryChatDetection();
  }

  function getLegacyChatButton(element) {
    if (!element || !element.shadowRoot) {
      return null;
    }

    const shadowRoot = element.shadowRoot;

    if (isLegacyChatOpen(element)) {
      return findInOpenShadowRoots(
        shadowRoot,
        'button.chat-app--close-button, button[aria-label*="close" i], button[name="closeChat"]'
      );
    }

    return findInOpenShadowRoots(
      shadowRoot,
      'button.chat-toggle, button[data-spec="toggle-button"], button.icon-only'
    );
  }

  function syncAfterToggle() {
    window.setTimeout(syncChatState, 0);
    window.setTimeout(syncChatState, 150);
    window.setTimeout(syncChatState, 600);
    window.setTimeout(syncChatState, 1500);
  }

  function toggleShopifyChat() {
    refreshChatElement();

    if (!chatElement) {
      scheduleChatDetection();
      return;
    }

    if (isNewChatElement(chatElement)) {
      chatElement.toggleAttribute('open');
      syncAfterToggle();
      return;
    }

    const button = getLegacyChatButton(chatElement);

    if (!button) {
      scheduleChatDetection();
      return;
    }

    button.click();
    syncAfterToggle();
  }

  function handleCustomIconClick(event) {
    event.preventDefault();
    event.stopPropagation();
    toggleShopifyChat();
  }

  function handleCustomIconKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    toggleShopifyChat();
  }

  function setupDocumentObserver() {
    if (documentObserver || !document.body) {
      return;
    }

    documentObserver = new MutationObserver(function (mutations) {
      const chatDomChanged = mutations.some(function (mutation) {
        return Array.from(mutation.addedNodes).some(function (node) {
          return (
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches(NEW_CHAT_SELECTOR) ||
              node.matches(LEGACY_CHAT_SELECTOR) ||
              node.querySelector(NEW_CHAT_SELECTOR) ||
              node.querySelector(LEGACY_CHAT_SELECTOR))
          );
        });
      });

      if (chatDomChanged || (chatElement && !chatElement.isConnected)) {
        scheduleChatDetection();
      }
    });

    documentObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function initCustomChat() {
    customChatIcon = document.getElementById(CUSTOM_ICON_ID);

    if (!customChatIcon || customChatIcon.dataset.chatInitialized === 'true') {
      return;
    }

    customChatIcon.dataset.chatInitialized = 'true';
    customChatIcon.addEventListener('click', handleCustomIconClick);
    customChatIcon.addEventListener('keydown', handleCustomIconKeydown);

    setupDocumentObserver();
    scheduleChatDetection();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomChat, { once: true });
  } else {
    initCustomChat();
  }
})();
